// Room 5's calls (the social area: what others see), through the doorway (doorway.ts): the feeds of Explore, the
// search page and the landing page (card R5-15). Each call sends exactly the request its screen sent before it moved
// here (tests/social-api.test.mjs), and with the social area's switch on is answered by v2 in v1's shape
// (social-v2.ts): one feed action (`GET /v2/feed`) in place of v1's four, no `safe` sent (v2 reads safe mode from the
// person's standing), and every page full, as v2 applies the feed's rule itself. The picture page's, the cards' and
// the immersive feed's likes and views (card R5-16): v2's `PUT`/`DELETE /v2/media/{id}/like` and
// `PUT /v2/media/{id}/view`, and "did I like it" read from the picture's own read (`GET /v2/media/{id}`). A profile
// and the Following tab (card R5-17): a person (`GET /v2/people/{id}`), their posts and likes
// (`GET /v2/people/{id}/media`), follow and unfollow (`PUT`/`DELETE /v2/people/{id}/follow`), their followers and
// those they follow (`GET /v2/people/{id}/followers`, `.../following`), and the posts of people I follow
// (`GET /v2/feed/following`) on Explore, the search page and the landing page. The picture page's read
// (`readPicture`, plan task r5-picture-page): v2's `GET /v2/media/{id}` in v1's `fetch_image` shape, signed in by
// this area's switch, and without a sign-in (the page's server render, a signed-out browser) by its own,
// `picture_signed_out`, which places pictures by id.
//
// The site's picture count (`/api/total_images`, R5-06; plan task r5-site-count): v2's `GET /v2/stats`, its
// `all_items`, v1's own number. Stay on v1 whatever the switch says, until v2 has them: search (`/api/search`, v2's
// search is being built), the picture page's related pictures (search), and the profile's private gallery and its
// folders (`get_profile_feed` with `feed_type: 'private'`, `get_unique_folders`: room 3's). Staff picks (`set_safe`,
// `add_to_featured`, `remove_from_featured`) were dropped (Andy, 23 Oct 2026): the website no longer calls them.
import { call, lazySide, signedInJson } from './doorway.ts'
import type { V2Side } from './doorway.ts'

const AREA = 'social'

type Sides = typeof import('./social-v2.ts')

/**
 * A call's v2 side, as `use` builds it from social-v2.ts, loaded only when the switch puts the visitor on v2. When
 * that module fails to load, the call goes to v1 at `path`, as it would have with the switch off.
 */
const onV2 = (path: string, use: (loaded: Sides) => V2Side): V2Side =>
  lazySide(() => import('./social-v2.ts'), use, path)

/** What Explore asks v1's `get_explore_feed` for, as it sends it. */
export type ExploreAsk = {
  last_doc_id: string | null
  limit: number
  safe: boolean | null
  model: string
  media_type: string
  sort_by: 'top' | 'trending' | 'newest'
  orientation: string
  date_range?: string
}

/** Explore's feed: a page `{ images, has_more }` of v1's picture documents. */
export const exploreFeed = (token: string, ask: ExploreAsk) =>
  call(
    AREA,
    '/api/get_explore_feed',
    {
      method: 'POST',
      headers: signedInJson(token),
      body: JSON.stringify(ask),
    },
    {
      token,
      side: onV2(
        '/api/get_explore_feed',
        (loaded) => (t, init) => loaded.exploreFeed(t, init, ask),
      ),
    },
  )

/** The search and landing pages' three feeds: v1's `get_top_feed`, `get_newest_feed` and `get_video_feed`. */
export type LegacyFeed = 'top' | 'newest' | 'video'

/** What the search and landing pages send each of the three, as they send it: every list's last id. */
export type LegacyAsk = {
  last_doc_id_newest: string | null
  last_doc_id_video: string | null
  last_doc_id: string | null
  safe: boolean | null
  model: string
}

/** The search or landing page's `feed`: a page `{ images, has_more }` of v1's picture documents. */
export const legacyFeed = (token: string, feed: LegacyFeed, ask: LegacyAsk) =>
  call(
    AREA,
    `/api/get_${feed}_feed`,
    {
      method: 'POST',
      body: JSON.stringify(ask),
      headers: signedInJson(token),
    },
    {
      token,
      side: onV2(
        `/api/get_${feed}_feed`,
        (loaded) => (t, init) => loaded.legacyFeed(t, init, feed, ask),
      ),
    },
  )

/** A feed item or the picture page's picture, as the screen holds it: the like and view calls send it whole. */
type Picture = { id: string } & Record<string, unknown>

/** Whether the person likes picture `imageId`: `{ liked }`. */
export const checkLiked = (token: string, imageId: string) =>
  call(
    AREA,
    '/api/check_liked_image',
    {
      method: 'POST',
      headers: signedInJson(token),
      body: JSON.stringify({ image_id: imageId }),
    },
    {
      token,
      side: onV2(
        '/api/check_liked_image',
        (loaded) => (t, init) => loaded.checkLiked(t, init, imageId),
      ),
    },
  )

/** Likes the picture (`liked` true) or takes the like back (`liked` false). */
export const setLiked = (token: string, imageData: Picture, liked: boolean) => {
  const path = liked ? '/api/like_image' : '/api/unlike_image'
  return call(
    AREA,
    path,
    {
      method: 'POST',
      headers: signedInJson(token),
      body: JSON.stringify({ imageData }),
    },
    {
      token,
      side: onV2(
        path,
        (loaded) => (t, init) => loaded.setLiked(t, init, imageData.id, liked),
      ),
    },
  )
}

/** Counts that the person has seen the picture, when its page opens. */
export const viewImage = (token: string, imageData: Picture) =>
  call(
    AREA,
    '/api/view_image',
    {
      method: 'POST',
      headers: signedInJson(token),
      body: JSON.stringify({ imageData }),
    },
    {
      token,
      side: onV2(
        '/api/view_image',
        (loaded) => (t, init) => loaded.viewImage(t, init, imageData.id),
      ),
    },
  )

/** What a profile asks v1's `get_profile_feed` for, as it sends it. */
export type ProfileAsk = {
  target_uid: string
  feed_type: 'posts' | 'likes' | 'private'
  limit: number
  last_doc_id: string | null
  folder: string | null
  sort: 'newest' | 'oldest' | 'most_liked' | 'most_viewed'
}

/**
 * A profile's posts, likes or private gallery: a page `{ images, has_more, next_cursor }` of v1's picture documents.
 * The private gallery is room 3's (ai_app_backend plan task r3-folders): it moves with the Create page's pictures, by
 * the `create` area's switch (`v2_room3_pictures`), not the social area's.
 */
export const profileFeed = (
  token: string,
  ask: ProfileAsk,
  signal?: AbortSignal,
) => {
  const init: RequestInit = {
    method: 'POST',
    headers: signedInJson(token),
    ...(signal ? { signal } : {}),
    body: JSON.stringify(ask),
  }
  return call(
    ask.feed_type === 'private' ? 'create' : AREA,
    '/api/get_profile_feed',
    init,
    {
      token,
      side: onV2(
        '/api/get_profile_feed',
        (loaded) => (t, i) => loaded.profileFeed(t, i, ask),
      ),
    },
  )
}

/** A person as their profile shows them: v1's `{ status, profile }`. */
export const profileInfo = (token: string, targetUid: string) =>
  call(
    AREA,
    '/api/get_profile_info',
    {
      method: 'POST',
      headers: signedInJson(token),
      body: JSON.stringify({ target_uid: targetUid }),
    },
    {
      token,
      side: onV2(
        '/api/get_profile_info',
        (loaded) => (t, init) => loaded.profileInfo(t, init, targetUid),
      ),
    },
  )

/** Follows the person (`follow` true) or stops following them (`follow` false). */
export const setFollowing = (token: string, uid: string, follow: boolean) => {
  const path = follow ? '/api/follow_user' : '/api/unfollow_user'
  return call(
    AREA,
    path,
    {
      method: 'POST',
      headers: signedInJson(token),
      body: JSON.stringify(
        follow ? { userToFollowUID: uid } : { userToUnfollowUID: uid },
      ),
    },
    {
      token,
      side: onV2(
        path,
        (loaded) => (t, init) => loaded.setFollowing(t, init, uid, follow),
      ),
    },
  )
}

/** What a profile asks v1's `fetch_following_data` for, as it sends it. */
export type FollowListAsk = {
  target_uid: string
  list_type: 'followers' | 'following'
  offset: number
  limit: number
}

/** A page of a person's followers, or of those they follow: v1's `{ users, has_more, … }`. */
export const followList = (token: string, ask: FollowListAsk) =>
  call(
    AREA,
    '/api/fetch_following_data',
    {
      method: 'POST',
      headers: signedInJson(token),
      body: JSON.stringify(ask),
    },
    {
      token,
      side: onV2(
        '/api/fetch_following_data',
        (loaded) => (t, init) => loaded.followList(t, init, ask),
      ),
    },
  )

/** The Following tab of Explore, the search page and the landing page: v1's list of picture documents, whole. */
export const followingFeed = (token: string) =>
  call(
    AREA,
    '/api/search_by_uids',
    {
      method: 'POST',
      headers: signedInJson(token),
    },
    {
      token,
      side: onV2(
        '/api/search_by_uids',
        (loaded) => (t, init) => loaded.followingFeed(t, init),
      ),
    },
  )

/**
 * The picture page's picture: v1's `fetch_image` document `{ id, uid, username, image_url, og_image_url, prompt, … }`,
 * or its `404 { error }`. `token` is the person's Firebase ID token, or null without a sign-in (the page's server
 * render, a signed-out browser). Signed in, it follows this area's switch (v1 reads v2's pictures too, from the
 * shared database, so with the switch off nothing changes for anyone); without a sign-in, the signed-out picture page's switch, which places the picture by its id, so the server render
 * and every signed-out browser read the same picture from the same API. The v1 request is as the page sent it: the
 * owner's token finds their private copy, `user_uid` says whose gallery to look in.
 */
export const readPicture = (
  token: string | null,
  imageId: string,
  userUid?: string,
) => {
  const side = onV2(
    '/api/fetch_image',
    (loaded) => (t, init) =>
      loaded.readPicture(token === null ? null : t, init, imageId),
  )
  const init: RequestInit = {
    method: 'POST',
    headers: token
      ? signedInJson(token)
      : { 'Content-Type': 'application/json' },
    body: JSON.stringify({ image_id: imageId, user_uid: userUid }),
  }
  if (token === null)
    // A signed-out read has no person to place: the switch reads the picture's id as its `token`, and the side never
    // sends it (it asks v2 without a sign-in).
    return call('picture_signed_out', '/api/fetch_image', init, {
      token: imageId,
      side,
    })
  return call(AREA, '/api/fetch_image', init, { token, side })
}

/**
 * The site's picture count, `{ total_images }` (Explore, the search page and the landing page's header), signed in. On
 * v2, `GET /v2/stats`'s `all_items`: every picture and clip kept, as v1 counts them.
 */
export const siteCount = (token: string) =>
  call(
    AREA,
    '/api/total_images',
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
    {
      token,
      side: onV2('/api/total_images', (loaded) => loaded.siteCount),
    },
  )
